import React, { useMemo, useState } from "react";
import { DatePeriodPicker } from "./DatePeriodPicker";
import { FiDownload } from "react-icons/fi";

type Breakdown = {
  name: string;
  completed: number;
  breached: number;
  over60: number;
};

type FastOrder = {
  orderId: string;
  customer: string;
  brand: string;
  location: string;
  age: number;
};

type FastestByLocation = {
  location: string;
  orders: FastOrder[];
};

type Day = {
  date: string;
  totals: Omit<Breakdown, "name">;
  brands: Breakdown[];
  locations: Breakdown[];
  lateOrders: {
    orderId: string;
    customer: string;
    brand: string;
    location: string;
    age: string;
  }[];
  fastestByLocation?: FastestByLocation[];
};

type Props = {
  days: Day[];
};

type Tone = "good" | "warn" | "bad";

const TONES: Record<Tone, { fg: string; bg: string }> = {
  good: { fg: "var(--ct-ok)", bg: "var(--ct-ok-bg)" },
  warn: { fg: "var(--ct-warn)", bg: "var(--ct-warn-bg)" },
  bad: { fg: "var(--ct-crit)", bg: "var(--ct-crit-bg)" },
};

function StatCard({
  label,
  value,
  tone,
  alwaysOn = false,
}: {
  label: string;
  value: number;
  tone: Tone;
  alwaysOn?: boolean;
}) {
  const live = alwaysOn || value > 0;
  const t = TONES[tone];

  return (
    <div
      style={{
        flex: "1 1 200px",
        minWidth: 200,
        borderRadius: 10,
        border: "1px solid var(--ct-line)",
        background: "var(--ct-surface)",
        padding: "14px 16px",
      }}
    >
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 500,
          color: live ? "var(--ct-ink-2)" : "var(--ct-ink-3)",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        className="ct-num"
        style={{
          display: "inline-block",
          fontSize: 26,
          fontWeight: 650,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          padding: live ? "2px 10px" : "2px 0",
          borderRadius: 8,
          color: live ? t.fg : "var(--ct-ink-3)",
          background: live ? t.bg : "transparent",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Cell({ value, tone }: { value: number; tone: Tone }) {
  const live = value > 0;
  return (
    <td
      className="ct-num"
      style={{
        padding: "13px 16px",
        whiteSpace: "nowrap",
        fontSize: 13.5,
        fontWeight: live ? 600 : 400,
        color: live ? TONES[tone].fg : "var(--ct-ink-3)",
      }}
    >
      {value}
    </td>
  );
}

function BreakdownTable({
  title,
  firstColLabel,
  rows,
}: {
  title: string;
  firstColLabel: string;
  rows: Breakdown[];
}) {
  const headers = [firstColLabel, "Completed", "Breached", "Over 1 hour"];

  return (
    <div
      style={{
        borderRadius: 10,
        border: "1px solid var(--ct-line)",
        background: "var(--ct-surface)",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div
        style={{
          padding: "13px 16px",
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "var(--ct-ink)",
          borderBottom: "1px solid var(--ct-line)",
        }}
      >
        {title}
      </div>

      <div style={{ overflowX: "auto", width: "100%" }}>
        <table
          style={{
            width: "100%",
            minWidth: 480,
            borderCollapse: "collapse",
            fontSize: 13.5,
          }}
        >
          <thead>
            <tr style={{ background: "var(--ct-surface-2)" }}>
              {headers.map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "10px 16px",
                    textAlign: "left",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "var(--ct-ink-3)",
                    borderBottom: "1px solid var(--ct-line)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={r.name + idx}
                style={{
                  borderBottom:
                    idx === rows.length - 1
                      ? "none"
                      : "1px solid var(--ct-line-soft)",
                }}
              >
                <td
                  style={{
                    padding: "13px 16px",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                    fontSize: 13.5,
                    color: "var(--ct-ink)",
                  }}
                >
                  {r.name}
                </td>
                <td
                  className="ct-num"
                  style={{
                    padding: "13px 16px",
                    whiteSpace: "nowrap",
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "var(--ct-ink)",
                  }}
                >
                  {r.completed}
                </td>
                <Cell value={r.breached} tone="bad" />
                <Cell value={r.over60} tone="warn" />
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    textAlign: "center",
                    padding: "40px 16px",
                    fontSize: 13.5,
                    color: "var(--ct-ink-3)",
                  }}
                >
                  No data for this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type TaggedFastOrder = FastOrder & { date: string };

function FastestOrdersTable({ days }: { days: Day[] }) {
  const rows: TaggedFastOrder[] = useMemo(() => {
    const byLoc: Record<string, TaggedFastOrder[]> = {};
    for (const d of days) {
      for (const entry of d.fastestByLocation || []) {
        if (!byLoc[entry.location]) byLoc[entry.location] = [];
        for (const o of entry.orders) {
          byLoc[entry.location].push({ ...o, date: d.date });
        }
      }
    }
    const all: TaggedFastOrder[] = [];
    for (const [, orders] of Object.entries(byLoc)) {
      orders.sort((a, b) => a.age - b.age);
      all.push(...orders.slice(0, 3));
    }
    all.sort((a, b) => a.age - b.age);
    return all;
  }, [days]);

  if (rows.length === 0) return null;

  const HEADERS = ["Date", "Order", "Age", "Customer", "Brand", "Location"];

  return (
    <div
      style={{
        borderRadius: 10,
        border: "1px solid var(--ct-line)",
        background: "var(--ct-surface)",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div
        style={{
          padding: "13px 16px",
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "var(--ct-ink)",
          borderBottom: "1px solid var(--ct-line)",
        }}
      >
        Fastest 3 orders by location
      </div>

      <div style={{ overflowX: "auto", width: "100%" }}>
        <table
          style={{
            width: "100%",
            minWidth: 700,
            borderCollapse: "collapse",
            fontSize: 13.5,
          }}
        >
          <thead>
            <tr style={{ background: "var(--ct-surface-2)" }}>
              {HEADERS.map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "10px 16px",
                    textAlign: "left",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "var(--ct-ink-3)",
                    borderBottom: "1px solid var(--ct-line)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={r.date + r.orderId + idx}
                style={{
                  borderBottom:
                    idx === rows.length - 1
                      ? "none"
                      : "1px solid var(--ct-line-soft)",
                  transition: "background .12s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--ct-row-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <td
                  className="ct-num"
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    color: "var(--ct-ink-3)",
                  }}
                >
                  {r.date}
                </td>

                <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                  <span
                    className="ct-num"
                    style={{
                      fontWeight: 600,
                      color: "var(--ct-accent)",
                    }}
                  >
                    {r.orderId.length > 8
                      ? r.orderId.slice(0, 8) + "…"
                      : r.orderId}
                  </span>
                </td>

                <td
                  className="ct-num"
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "var(--ct-ok)",
                  }}
                >
                  {r.age}m
                </td>

                <td
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                    color: "var(--ct-ink)",
                  }}
                >
                  {r.customer}
                </td>

                <td
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    color: "var(--ct-ink)",
                  }}
                >
                  {r.brand}
                </td>

                <td
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    color: "var(--ct-ink-2)",
                  }}
                >
                  {r.location}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DayReport({ day }: { day: Day }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--ct-ink-2)",
          marginBottom: 12,
        }}
      >
        {day.date}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <StatCard
          label="Completed orders"
          value={day.totals.completed}
          tone="good"
          alwaysOn
        />
        <StatCard
          label="Breached orders"
          value={day.totals.breached}
          tone="bad"
        />
        <StatCard
          label="Over 1 hour"
          value={day.totals.over60}
          tone="warn"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <BreakdownTable
          title="By brand"
          firstColLabel="Brand"
          rows={day.brands}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <BreakdownTable
          title="By location"
          firstColLabel="Location"
          rows={day.locations}
        />
      </div>

      <FastestOrdersTable days={[day]} />
    </div>
  );
}

export function SummaryDashboard({ days }: Props) {
  // null range = single-day stepping at dayIndex; a range = custom period from the picker
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [dayIndex, setDayIndex] = useState(0); // 0 = latest day

  const newestDate = days[0]?.date || "";
  const oldestDate = days[days.length - 1]?.date || "";
  const lastIndex = Math.max(0, days.length - 1);
  const safeIndex = Math.min(dayIndex, lastIndex);

  const usingRange = range !== null;

  const shown = usingRange
    ? days.filter((d) => d.date >= range!.from && d.date <= range!.to)
    : days[safeIndex]
    ? [days[safeIndex]]
    : [];

  const stepOlder = () => {
    setRange(null);
    setDayIndex((i) => Math.min(i + 1, lastIndex));
  };
  const stepNewer = () => {
    setRange(null);
    setDayIndex((i) => Math.max(i - 1, 0));
  };
  const goLatest = () => {
    setRange(null);
    setDayIndex(0);
  };

  const atNewest = !usingRange && safeIndex === 0;
  const atOldest = !usingRange && safeIndex >= lastIndex;

  const dayLabel = usingRange
    ? range!.from === range!.to
      ? range!.from
      : `${range!.from} → ${range!.to}`
    : days[safeIndex]?.date || "Latest";

  const navBtn = (disabled: boolean): React.CSSProperties => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1px solid var(--ct-line)",
    background: "var(--ct-surface)",
    color: disabled ? "var(--ct-ink-3)" : "var(--ct-ink-2)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 16,
    display: "grid",
    placeItems: "center",
    fontFamily: "inherit",
  });

  const exportReport = () => window.print();

  return (
    <main style={{ padding: "20px 24px 40px" }}>
      <style>{`
        @media print {
          nav, header, .ct-no-print { display: none !important; }
          body, html, #root, .App { background: #fff !important; }
          main { padding: 0 !important; }
          div[style*="margin-left"] { margin-left: 0 !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
        }
      `}</style>

      <div
        className="ct-no-print"
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 13, color: "var(--ct-ink-3)" }}>
          {usingRange ? "Selected period" : atNewest ? "Latest capture" : dayLabel}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Day stepper: ‹ older · date · newer › */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={stepOlder}
              disabled={atOldest}
              title="Older day"
              style={navBtn(atOldest)}
            >
              ‹
            </button>

            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ct-ink)",
                minWidth: 150,
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {atNewest ? `${dayLabel} · latest` : dayLabel}
            </div>

            <button
              onClick={stepNewer}
              disabled={atNewest}
              title="Newer day"
              style={navBtn(atNewest)}
            >
              ›
            </button>

            {!atNewest && (
              <button
                onClick={goLatest}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--ct-line)",
                  background: "var(--ct-surface)",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--ct-ink-2)",
                  fontFamily: "inherit",
                }}
              >
                Latest
              </button>
            )}
          </div>

          <DatePeriodPicker
            buttonLabel={dayLabel}
            defaultFrom={oldestDate}
            defaultTo={newestDate}
            onApply={(from, to) => {
              setDayIndex(0);
              setRange({ from, to });
            }}
          />

          <button
            onClick={exportReport}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 500,
              padding: "7px 13px",
              borderRadius: 8,
              border: "1px solid var(--ct-line)",
              background: "var(--ct-surface)",
              color: "var(--ct-ink)",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--ct-accent)";
              e.currentTarget.style.color = "var(--ct-accent)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--ct-line)";
              e.currentTarget.style.color = "var(--ct-ink)";
            }}
          >
            <FiDownload size={14} />
            Export report
          </button>
        </div>
      </div>

      {shown.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "64px 16px",
            fontSize: 13.5,
            color: "var(--ct-ink-3)",
          }}
        >
          No summaries for this period.
        </div>
      )}

      {shown.map((day) => (
        <DayReport key={day.date} day={day} />
      ))}
    </main>
  );
}
